import { format, subDays } from "date-fns"
import React, { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { ReportFilters } from "@component/filters/report-filters.tsx"
import { useModal } from "@component/modal.ts"
import { Column, ReactTailwindTable, useTable } from "@component/react-tailwind-table.tsx"
import { useFilters, ParamType } from "@component/use-filter.ts"
import { RejectionModal } from "./rejection-modal.tsx"
import {
  useGetOperatorCallsQuery,
  useSummarizeCallMutation,
  useUpdateFileApprovalAndCommentMutation,
} from "./service.ts"
import { useGetAverageScoresByCriteriaQuery } from "../home/service"
import { ShowMoreText } from "./show-more-text.tsx"
import { formatPhoneNumber } from "@/utils/phone-number-formatter.ts"
import FactDetailsModal from "./fact-details-modal.tsx"
import { SummaryModal } from "./summary-modal.tsx"
import { useSelection } from "../../app/department-context.tsx"

const prepareUrl = (url: string) => {
  if (url.startsWith("https://drive.google.com")) {
    // Try to extract file ID from URL formats like:
    // - https://drive.google.com/file/d/<ID>/view?usp=sharing
    // - https://drive.google.com/open?id=<ID>
    // - https://drive.google.com/uc?id=<ID>&export=download

    const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/) || url.match(/[?&]id=([a-zA-Z0-9_-]+)/)

    const fileId = fileIdMatch ? fileIdMatch[1] : null

    if (!fileId) {
      console.warn("Could not extract Google Drive file ID:", url)
      return url // fallback to original
    }

    // Construct proxy URL
    return `/api/audio/?type=googledrive&id=${fileId}`
  }
  return url
}

interface OperatorCallRow {
  file_id: number
  file_date: string
  client_phone_number: string
  contact_phone_number: string
  operator_id: number
  operator_name: string
  call_type: string
  score: number
  url: string | null
  external_file_id: string | null
  text: string
  summary_data: { text?: string; error?: string } | null
  criteria: { name: string; score: number }[]
  is_approved: boolean | null
  approve_comment: string
}

interface AverageScoreRow {
  name: string
  averageScore: number
}

interface AudioCellProps {
  url?: string | null
  externalFileId?: string | null
  fileId: number
}

const AudioCell = React.memo(({ url, externalFileId, fileId }: AudioCellProps) => {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(url ? prepareUrl(url) : null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const resolveUrl = () => {
    setLoading(true)
    setError(false)
    fetch(`${import.meta.env.VITE_BACKEND_URL}/api/audio/resolve?fileId=${fileId}`, { redirect: "follow" })
      .then(res => {
        if (!res.ok) throw new Error(`resolve failed: ${res.status}`)
        return res.url
      })
      .then(finalUrl => setResolvedUrl(finalUrl))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  if (resolvedUrl) {
    return <audio controls src={resolvedUrl} />
  }
  if (!externalFileId) {
    return null
  }
  if (loading) {
    return <span className="loading loading-spinner loading-sm" />
  }
  if (error) {
    return (
      <button className="btn btn-xs btn-error" onClick={resolveUrl}>
        Ошибка, повторить
      </button>
    )
  }
  return (
    <button className="btn btn-xs btn-outline mb-2" onClick={resolveUrl}>
      Загрузить аудио
    </button>
  )
})

export const OperatorReport = () => {
  const { selectedDepartment } = useSelection()
  const { setPagination, pagination } = useTable({})
  const [summarizeCall, { isLoading: isSummarizing }] = useSummarizeCallMutation()

  const averageScoresByCriteriaTable = useTable({ defaultPageSize: 10 })
  const { isOpen, openModal, closeModal, data } = useModal<{ file_id: number; approve_comment: string }>()
  const {
    isOpen: isFactDetailsOpen,
    openModal: openFactDetailsModal,
    closeModal: closeFactDetailsModal,
    data: factDetailsData,
  } = useModal<{ criteria: { name: string; score: number }; file_id: number }>()
  const {
    isOpen: isSummaryOpen,
    openModal: openSummaryModal,
    closeModal: closeSummaryModal,
    data: summaryData,
  } = useModal<{ text?: string; error?: string }>()
  const [updateFileApprovalAndComment, { isLoading: isSubmitting }] = useUpdateFileApprovalAndCommentMutation()
  const filterParams = useMemo(() => {
    return {
      params: {
        operatorId: "number",
        startDate: "string",
        endDate: "string",
        callTypeId: "number",
        departmentId: "number",
        intentDirection: "string",
        criteria: "json",
        text: "string",
        debugSample: "boolean",
      } as Record<string, ParamType>,
      defaults: {
        departmentId: selectedDepartment?.id,
        startDate: format(subDays(new Date(), 30), "yyyy-MM-dd"),
        endDate: format(new Date(), "yyyy-MM-dd"),
        intentDirection: "",
        criteria: [],
        text: "",
      },
    }
  }, [selectedDepartment?.id])
  const { filters, updateFilters, serializableFilters } = useFilters(filterParams)

  const onSummarize = useCallback(
    (row: OperatorCallRow) => {
      if (row.summary_data) {
        openSummaryModal(row.summary_data)
      } else {
        openSummaryModal({})
        summarizeCall({ fileId: row.file_id })
          .unwrap()
          .then(res => {
            openSummaryModal({ text: res.text })
          })
          .catch(err => openSummaryModal({ error: err?.data?.message ?? "Ошибка суммаризации" }))
      }
    },
    [openSummaryModal, summarizeCall],
  )
  const approve = useCallback(
    async ({ id }: { id: number }) => {
      if (data) {
        try {
          await updateFileApprovalAndComment({
            id,
            is_approved: true,
            comment: "",
          }).unwrap()
          toast("Сохранено")
        } catch (error) {
          console.error(error)
          toast("Error saving", { type: "error" })
        }
      }
    },
    [data, updateFileApprovalAndComment],
  )
  const columns: Column<OperatorCallRow>[] = useMemo(
    () => [
      {
        accessorKey: "file_id",
        size: 50,
        header: "ID",
      },
      {
        accessorKey: "file_date",
        size: 100,
        header: "Дата и время",
        cell: ({ row }) => format(new Date(row.original.file_date.replace("Z", "")), "dd.MM.yy\n HH:mm:ss"),
      },
      {
        accessorKey: "client_phone_number",
        header: "Собеседники",
        enableSorting: false,
        size: 180,
        cell: ({ row }) => {
          const rawPhone = row.original.client_phone_number || row.original.contact_phone_number
          return (
            <span>
              <button
                className="underline decoration-dotted cursor-pointer hover:text-teal-700"
                title="Найти по номеру"
                onClick={() => updateFilters({ text: rawPhone })}
              >
                {formatPhoneNumber(rawPhone)}
              </button>
              {" - "}
              <button
                className="underline decoration-dotted cursor-pointer hover:text-teal-700"
                title="Фильтровать по оператору"
                onClick={() => updateFilters({ operatorId: String(row.original.operator_id) })}
              >
                {row.original.operator_name}
              </button>
            </span>
          )
        },
      },
      {
        accessorKey: "call_type",
        header: "Тип звонка",
        size: 100,
      },
      {
        accessorKey: "score",
        header: "Оценка",
        size: 50,
      },
      {
        header: "Текст",
        size: undefined,
        cell: ({ row }) => {
          return (
            <div>
              <AudioCell
                url={row.original.url}
                externalFileId={row.original.external_file_id}
                fileId={row.original.file_id}
              />
              <ShowMoreText text={row.original.text} onSummarize={onSummarize} row={row.original} />
            </div>
          )
        },
      },
      {
        header: "Критерии",
        size: 200,
        cell: ({ row }) => (
          <ul className="space-y-2">
            {row.original.criteria
              .filter(c => c.score !== null)
              .map(c => {
                const scoreColor = c.score >= 0.8 ? "bg-green-500" : c.score >= 0.5 ? "bg-yellow-500" : "bg-red-500"
                const backdropClass = c.score === 0 ? "bg-red-500" : "bg-gray-200"
                return (
                  <li key={c.name} className="text-xs">
                    <button
                      onClick={() => openFactDetailsModal({ criteria: c, file_id: row.original.file_id })}
                      className={"w-full cursor-pointer"}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <span className="truncate" title={c.name}>
                          {c.name}
                        </span>
                        <span className="font-bold">{(c.score * 100).toFixed(0)}%</span>
                      </div>
                      <div className={`w-full ${backdropClass} rounded-full h-1.5 dark:bg-gray-700`}>
                        <div
                          className={`${scoreColor} h-1.5 rounded-full`}
                          style={{ width: `${c.score * 100}%` }}
                        ></div>
                      </div>
                    </button>
                  </li>
                )
              })}
          </ul>
        ),
      },

      {
        header: "Одобрено",
        accessorKey: "file_feature2.is_approved",
        size: 50,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center space-x-2">
            <button
              className={`${row.original.is_approved ? "bg-green-500" : "bg-gray-300"} text-white p-1 rounded-full cursor-pointer`}
              onClick={() => approve({ id: row.original.file_id })}
            >
              {isSubmitting ? (
                <span className="loading loading-spinner loading-xs m-2" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
            <button
              className={`${row.original.is_approved === false ? "bg-red-500" : "bg-gray-300"} text-white p-1 rounded-full cursor-pointer`}
              onClick={() => {
                openModal(row.original)
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        ),
      },
    ],
    [approve, isSubmitting, onSummarize, openFactDetailsModal, openModal, updateFilters],
  )

  const averageScoresByCriteriaColumns: Column<AverageScoreRow>[] = [
    { header: "Критерий", accessorKey: "name", enableSorting: false },
    {
      header: "Средний балл",
      accessorKey: "averageScore",
      enableSorting: false,
      cell: ({ row }) => `${(row.original.averageScore * 100).toFixed(0)}%`,
    },
  ]

  const handleSubmit = async (input: { comment: string }) => {
    if (data) {
      try {
        await updateFileApprovalAndComment({
          id: data.file_id as number,
          is_approved: false,
          comment: input.comment,
        }).unwrap()
        closeModal()
        toast("Сохранено")
      } catch (error) {
        console.error(error)
        toast("Error saving", { type: "error" })
      }
    }
  }
  const renderTable = useMemo(() => {
    return (
      <ReactTailwindTable<OperatorCallRow>
        setPagination={setPagination}
        pagination={pagination}
        query={useGetOperatorCallsQuery}
        queryParams={serializableFilters}
        columns={columns}
        rowId={"file_id"}
        downloadable
        downloadLimit={1000}
      />
    )
  }, [columns, pagination, serializableFilters, setPagination])
  return (
    <div className="p-4 h-full">
      <RejectionModal
        isOpen={isOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        data={data ?? undefined}
        isSubmitting={isSubmitting}
      />
      <FactDetailsModal isOpen={isFactDetailsOpen} onClose={closeFactDetailsModal} data={factDetailsData ?? undefined} />
      <SummaryModal
        isOpen={isSummaryOpen}
        onClose={closeSummaryModal}
        text={summaryData?.text}
        isLoading={isSummarizing && !summaryData?.text && !summaryData?.error}
        error={summaryData?.error}
      />
      <h1 className="text-xl sm:text-2xl font-bold text-center text-teal-800 mb-2">Отчет по оператору</h1>
      <ReportFilters
        filters={filters}
        updateFilters={updateFilters}
        showCallTypeFilter
        showOperatorFilter
        showIntentDirectionFilter
        showCriteriaFilter
        showTextFilter
        showDebugSampleFilter
      />
      {filters.operatorId && (
        <div className="collapse collapse-arrow border-base-300 border mb-6">
          <input type="checkbox" name="criteria-accordion" />
          <div className="collapse-title text-xl font-medium">Оценка по критериям (средние баллы)</div>
          <div className="collapse-content">
            <ReactTailwindTable
              columns={averageScoresByCriteriaColumns}
              query={useGetAverageScoresByCriteriaQuery}
              queryParams={{ ...filters }}
              visibilityOptions={{
                disablePagination: true,
              }}
              {...averageScoresByCriteriaTable}
              tableId="averageScoresByCriteriaTable"
            />
          </div>
        </div>
      )}
      <div className="flex flex-row gap-4">
        <div className=" flex-1">{renderTable}</div>
      </div>
    </div>
  )
}
